import type { IConnectorRepository } from "@/repositories/ConnectorRepository";
import type {
  ConnectorConfig,
  ConnectorType,
  SaveConnectorRequest,
  SiteMappingPair,
  SiteMappingWorkspace,
  TestConnectorRequest,
  TestConnectorResponse,
} from "@/types/connector.type";

export interface IConnectorUseCase {
  getConnectors(): Promise<ConnectorConfig[]>;
  getConnector(connectorType: ConnectorType): Promise<ConnectorConfig | null>;
  /** Fetch existing connector binding for a CTMS study UUID and connector type. */
  getConnectorByStudy(
    studyId: string,
    connectorType?: ConnectorType,
  ): Promise<ConnectorConfig | null>;
  saveConnector(payload: SaveConnectorRequest): Promise<ConnectorConfig>;
  /** Update an existing connector binding for a CTMS study UUID. */
  updateConnector(
    studyId: string,
    payload: SaveConnectorRequest,
  ): Promise<ConnectorConfig>;
  testConnector(payload: TestConnectorRequest): Promise<TestConnectorResponse>;
  deleteConnector(
    studyId: string,
    connectorType?: ConnectorType,
  ): Promise<void>;
  getSiteMappingWorkspace(
    studyId: string,
    connectorType: ConnectorType,
  ): Promise<SiteMappingWorkspace>;
  saveSiteMappings(
    studyId: string,
    connectorType: ConnectorType,
    mappings: SiteMappingPair[],
  ): Promise<SiteMappingWorkspace>;
  deleteSiteMapping(
    studyId: string,
    connectorType: ConnectorType,
    mappingId: string,
  ): Promise<SiteMappingWorkspace>;
}

export class ConnectorUseCase implements IConnectorUseCase {
  constructor(private readonly repository: IConnectorRepository) {}

  getConnectors(): Promise<ConnectorConfig[]> {
    return this.repository.getConnectors();
  }

  getConnector(connectorType: ConnectorType): Promise<ConnectorConfig | null> {
    return this.repository.getConnector(connectorType);
  }

  getConnectorByStudy(
    studyId: string,
    connectorType?: ConnectorType,
  ): Promise<ConnectorConfig | null> {
    return this.repository.getConnectorByStudy(studyId, connectorType);
  }

  saveConnector(payload: SaveConnectorRequest): Promise<ConnectorConfig> {
    return this.repository.saveConnector(payload);
  }

  testConnector(payload: TestConnectorRequest): Promise<TestConnectorResponse> {
    return this.repository.testConnector(payload);
  }

  updateConnector(
    studyId: string,
    payload: SaveConnectorRequest,
  ): Promise<ConnectorConfig> {
    return this.repository.updateConnector(studyId, payload);
  }

  deleteConnector(
    studyId: string,
    connectorType?: ConnectorType,
  ): Promise<void> {
    return this.repository.deleteConnector(studyId, connectorType);
  }

  getSiteMappingWorkspace(
    studyId: string,
    connectorType: ConnectorType,
  ): Promise<SiteMappingWorkspace> {
    return this.repository.getSiteMappingWorkspace(studyId, connectorType);
  }

  saveSiteMappings(
    studyId: string,
    connectorType: ConnectorType,
    mappings: SiteMappingPair[],
  ): Promise<SiteMappingWorkspace> {
    return this.repository.saveSiteMappings(studyId, connectorType, mappings);
  }

  deleteSiteMapping(
    studyId: string,
    connectorType: ConnectorType,
    mappingId: string,
  ): Promise<SiteMappingWorkspace> {
    return this.repository.deleteSiteMapping(studyId, connectorType, mappingId);
  }
}
